import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FaSyncAlt } from 'react-icons/fa';
import { getAdminUsers, updateAdminUserTier } from '../../service/adminService';
import SmallLoadingAlt from '../global/smallLoadingAlt';
import globalStyles from '../globals.module.css';
import componentStyles from './adminPage.module.css';

const TIER_OPTIONS = ['free', 'pro', 'admin'];

const formatDateTime = (value, emptyValue = '-') => {
    if (!value) return emptyValue;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return new Intl.DateTimeFormat(undefined, {
        dateStyle: 'medium',
        timeStyle: 'short'
    }).format(date);
};

const normalizeText = (value) => String(value || '').trim().toLowerCase();

const getLastLoginTime = (user) => {
    if (!user?.lastLoginAt) return 0;
    const time = new Date(user.lastLoginAt).getTime();
    return Number.isNaN(time) ? 0 : time;
};

const AdminUsers = ({ refreshToken = 0, adminKey = '' }) => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [tierFilter, setTierFilter] = useState('all');
    const [sessionFilter, setSessionFilter] = useState('all');
    const [tierUpdatingUserId, setTierUpdatingUserId] = useState(null);
    const [tierStatus, setTierStatus] = useState('');

    const fetchUsers = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const response = await getAdminUsers(adminKey);
            const data = response.data || {};
            if (!data.success) {
                setError(data.error || 'Could not load users.');
                setUsers([]);
                return;
            }
            setUsers(Array.isArray(data.users) ? data.users : []);
        } catch (err) {
            if (err.response?.status === 401) {
                setError('Admin key is missing or invalid.');
            } else {
                setError(err.response?.data?.error || 'Could not load users.');
            }
            setUsers([]);
        } finally {
            setLoading(false);
        }
    }, [adminKey]);

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers, refreshToken]);

    const filteredUsers = useMemo(() => {
        const normalizedSearch = normalizeText(searchTerm);

        return users
            .filter((user) => {
                if (!normalizedSearch) return true;
                return normalizeText(user.email).includes(normalizedSearch) ||
                    normalizeText(user.username).includes(normalizedSearch);
            })
            .filter((user) => {
                if (tierFilter === 'all') return true;
                return normalizeText(user.currentTier) === tierFilter;
            })
            .filter((user) => {
                if (sessionFilter === 'all') return true;
                const isActive = Boolean(user.hasActiveSession);
                return sessionFilter === 'active' ? isActive : !isActive;
            })
            .sort((a, b) => getLastLoginTime(b) - getLastLoginTime(a));
    }, [users, searchTerm, tierFilter, sessionFilter]);

    const hasFilters = Boolean(searchTerm.trim()) || tierFilter !== 'all' || sessionFilter !== 'all';

    const handleTierChange = async (user, nextTier) => {
        const userId = user?.id;
        const currentTier = user?.currentTier || 'free';
        if (!userId || nextTier === currentTier) {
            return;
        }

        setTierUpdatingUserId(userId);
        setTierStatus('');

        try {
            const response = await updateAdminUserTier(userId, nextTier, adminKey);
            const data = response.data || {};
            if (!data.success) {
                setTierStatus(data.error || 'Could not update user tier.');
                return;
            }

            const updatedUser = data.user || data.results?.user || null;
            setUsers((prevUsers) => prevUsers.map((existingUser) => {
                if (existingUser.id !== userId) {
                    return existingUser;
                }

                return {
                    ...existingUser,
                    ...(updatedUser || {}),
                    currentTier: updatedUser?.currentTier || updatedUser?.tier || nextTier,
                    lastEditAt: updatedUser?.lastEditAt || existingUser.lastEditAt
                };
            }));
            setTierStatus(`Updated ${user.email || user.username || 'user'} to ${nextTier.toUpperCase()}.`);
        } catch (err) {
            setTierStatus(err.response?.data?.error || 'Could not update user tier.');
        } finally {
            setTierUpdatingUserId(null);
        }
    };

    const renderControls = () => (
        <div className={componentStyles.usersControls}>
            <input
                className={`${globalStyles.standardInput} ${componentStyles.userSearchInput}`}
                type="search"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Search users"
                aria-label="Search users by email or username"
            />
            <select
                className={`${globalStyles.standardSelect} ${componentStyles.userFilterSelect}`}
                value={tierFilter}
                onChange={(event) => setTierFilter(event.target.value)}
                aria-label="Filter users by tier"
            >
                <option value="all">All tiers</option>
                {TIER_OPTIONS.map((tier) => (
                    <option key={tier} value={tier}>{tier.toUpperCase()}</option>
                ))}
            </select>
            <select
                className={`${globalStyles.standardSelect} ${componentStyles.userFilterSelect}`}
                value={sessionFilter}
                onChange={(event) => setSessionFilter(event.target.value)}
                aria-label="Filter users by active session"
            >
                <option value="all">All sessions</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
            </select>
        </div>
    );

    const renderContent = () => {
        if (error) {
            return (
                <div className={componentStyles.errorBlock}>
                    <p>{error}</p>
                    <button type="button" className={globalStyles.secondaryButton} onClick={fetchUsers}>
                        Retry
                    </button>
                </div>
            );
        }

        if (loading && users.length === 0) {
            return (
                <div className={globalStyles.loadingWrapper}>
                    <SmallLoadingAlt />
                </div>
            );
        }

        if (users.length === 0) {
            return (
                <div className={globalStyles.emptyState}>
                    No users found.
                </div>
            );
        }

        if (filteredUsers.length === 0) {
            return (
                <div className={globalStyles.emptyState}>
                    No users match the current filters.
                </div>
            );
        }

        return (
            <>
                <div className={componentStyles.summaryLine}>
                    {filteredUsers.length} of {users.length} user{users.length !== 1 ? 's' : ''}
                </div>
                {tierStatus && (
                    <div className={componentStyles.statusBlock}>
                        {tierStatus}
                    </div>
                )}
                <div className={componentStyles.tableWrap}>
                    <table className={`${componentStyles.adminTable} ${componentStyles.usersTable}`}>
                        <thead>
                            <tr>
                                <th>Email</th>
                                <th>Username</th>
                                <th>Tier</th>
                                <th>Created</th>
                                <th>Last Edited</th>
                                <th>Last Login</th>
                                <th>Session</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredUsers.map((user) => {
                                const tier = user.currentTier || 'free';
                                const hasActiveSession = Boolean(user.hasActiveSession);

                                return (
                                    <tr key={user.id}>
                                        <td data-label="Email">{user.email || '-'}</td>
                                        <td data-label="Username">{user.username || '-'}</td>
                                        <td data-label="Tier">
                                            <select
                                                className={`${globalStyles.standardSelect} ${componentStyles.tierSelect}`}
                                                value={tier}
                                                onChange={(event) => handleTierChange(user, event.target.value)}
                                                disabled={tierUpdatingUserId === user.id}
                                                aria-label={`Change tier for ${user.email || user.username || user.id}`}
                                            >
                                                {TIER_OPTIONS.map((option) => (
                                                    <option key={option} value={option}>
                                                        {option.toUpperCase()}
                                                    </option>
                                                ))}
                                            </select>
                                        </td>
                                        <td data-label="Created">{formatDateTime(user.createdAt)}</td>
                                        <td data-label="Last Edited">{formatDateTime(user.lastEditAt)}</td>
                                        <td data-label="Last Login">{formatDateTime(user.lastLoginAt, 'Never')}</td>
                                        <td data-label="Session">
                                            <span className={`${componentStyles.statusPill} ${hasActiveSession ? componentStyles.statusActive : componentStyles.statusInactive}`}>
                                                {hasActiveSession ? 'Active' : 'Inactive'}
                                            </span>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </>
        );
    };

    return (
        <div className={componentStyles.panelStack}>
            <section className={`${globalStyles.standardSection} ${componentStyles.adminSection}`}>
                <div className={componentStyles.sectionHeaderRow}>
                    <h3 className={globalStyles.sectionHeader}>Admin Users</h3>
                    <button
                        type="button"
                        className={globalStyles.iconButton}
                        onClick={fetchUsers}
                        disabled={loading}
                    >
                        <FaSyncAlt />
                        Refresh
                    </button>
                </div>
                {renderControls()}
                {hasFilters && (
                    <button
                        type="button"
                        className={`${globalStyles.textButton} ${componentStyles.clearFiltersButton}`}
                        onClick={() => {
                            setSearchTerm('');
                            setTierFilter('all');
                            setSessionFilter('all');
                        }}
                    >
                        Clear Filters
                    </button>
                )}
                {renderContent()}
            </section>
        </div>
    );
};

export default AdminUsers;
